import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { PowerOff, Settings } from 'lucide-react';

// The API answers 403 "This feature is not enabled for this application." when an
// opt-in feature (audit trail, groups, delegations, webhooks) is off for the app.
export const isFeatureOffError = (err) => {
    const status = err?.response?.status;
    const message = err?.response?.data?.message || '';
    return (status === 403 || status === 400) && /not enabled for this application/i.test(message);
};

/**
 * Tracks whether the gated feature is off for the app being viewed.
 * Wrap the page's list fetch in `guard(appId, promise)`; `off` is true only while the
 * last answer for *this* app was "feature not enabled".
 */
export const useFeatureGate = (appId) => {
    const [offFor, setOffFor] = useState('');

    const guard = useCallback(
        (requestAppId, promise) =>
            Promise.resolve(promise).then(
                (data) => {
                    setOffFor((cur) => (cur === requestAppId ? '' : cur));
                    return data;
                },
                (err) => {
                    if (isFeatureOffError(err)) setOffFor(requestAppId);
                    throw err;
                }
            ),
        []
    );

    return { off: Boolean(appId) && offFor === appId, guard };
};

/** Plain-language empty state for an opt-in feature that is turned off for the selected app. */
const FeatureOffState = ({ feature, appName, description, plural = false }) => {
    const name = appName || 'this app';
    return (
        <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
            <div className="mx-auto grid h-10 w-10 place-content-center rounded-full bg-slate-100 text-slate-600">
                <PowerOff className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-base font-semibold text-slate-900">
                {feature} {plural ? 'are' : 'is'} turned off for {name}
            </h2>
            {description && <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">{description}</p>}
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">
                An app admin can turn {plural ? 'them' : 'it'} on in the app’s settings.
            </p>
            <Link
                to={appName ? `/apps?search=${encodeURIComponent(appName)}` : '/apps'}
                className="mt-5 inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-sm font-medium text-slate-900 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
            >
                <Settings className="h-4 w-4" aria-hidden="true" />
                Open {appName ? `${appName} settings` : 'app settings'}
            </Link>
        </div>
    );
};

export default FeatureOffState;
